import React, { createContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './hooks/useAuth';
import { useQuery, useMutation } from 'convex/react';
import { api } from '@/lib/convex';
import { Conversation, Message } from '@/types/messaging';

interface MessagingContextType {
  conversations: Conversation[];
  activeConversation: Conversation | null;
  messages: Message[];
  isLoading: boolean;
  isSendingMessage: boolean;
  isLoadingMessages?: boolean;
  unreadCount: number;
  totalUnreadCount: number | undefined;
  typingUsers: Record<string, string[]>;
  // Actions
  loadConversations: () => Promise<void>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  setActiveConversation: (conversationId: string) => Promise<void>;
  sendMessage: (conversationId: string, content: string, files?: File[]) => Promise<void>;
  createConversation: (conversationData: string[] | { participantIds: string[]; conversationType?: string; groupName?: string }) => Promise<Conversation | null>;
  markAsRead: (conversationId: string) => Promise<void>;
  markMessagesAsRead: (conversationId: string, messageIds: string[]) => Promise<void>;
  clearActiveConversation: () => void;
  blockUser: (userId: string) => Promise<void>;
  unblockUser: (userId: string) => Promise<void>;
  reportMessage: (conversationId: string, messageId: string | null, reason: string) => Promise<void>;
  searchMessages: (query: string) => Promise<Message[]>;
  deleteConversation: (conversationId: string) => Promise<void>;
  sendTypingIndicator: (conversationId: string) => void;
  archiveConversation: (conversationId: string) => Promise<void>;
  unarchiveConversation: (conversationId: string) => Promise<void>;
  getArchivedConversations: () => Promise<Conversation[]>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
}

const MessagingContext = createContext<MessagingContextType | null>(null);

export { MessagingContext };

export const MessagingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);

  // Convex reactive queries
  const conversationsData = useQuery(
    api.messaging.listConversations,
    user ? { paginationOpts: { numItems: 50, cursor: null } } : "skip"
  );

  const totalUnreadCount = useQuery(
    api.messaging.getUnreadCount,
    user ? {} : "skip"
  );

  const activeConvData = useQuery(
    api.messaging.getConversation,
    activeConversationId ? { id: activeConversationId as any } : "skip"
  );

  const messagesData = useQuery(
    api.messaging.getMessages,
    activeConversationId
      ? { conversationId: activeConversationId as any, paginationOpts: { numItems: 50, cursor: null } }
      : "skip"
  );

  // Mutations
  const sendMessageMutation = useMutation(api.messaging.sendMessage);
  const createConversationMutation = useMutation(api.messaging.createConversation);
  const markAsReadMutation = useMutation(api.messaging.markAsRead);
  const editMessageMutation = useMutation(api.messaging.editMessage);
  const deleteMessageMutation = useMutation(api.messaging.deleteMessage);
  const archiveConversationMutation = useMutation(api.messaging.archiveConversation);
  const reportMessageMutation = useMutation(api.messaging.reportMessage);

  // Map Convex conversations to the existing Conversation type
  const conversations: Conversation[] = useMemo(() => {
    if (!conversationsData || !Array.isArray(conversationsData)) return [];
    return conversationsData.map((conv: any) => ({
      id: conv._id,
      created_at: new Date(conv._creationTime).toISOString(),
      updated_at: new Date(conv.updatedAt ?? conv._creationTime).toISOString(),
      last_message_at: conv.lastMessageAt ? new Date(conv.lastMessageAt).toISOString() : undefined,
      conversation_type: conv.conversationType,
      group_name: conv.title,
      group_description: conv.description,
      group_avatar_url: conv.avatarUrl,
      created_by: conv.createdBy,
      participants: (conv.participants ?? []).map((p: any) => ({
        id: p.participantRole ?? p._id ?? '',
        conversation_id: conv._id,
        user_id: p._id ?? p.profileId ?? '',
        joined_at: '',
        is_active: true,
        role: p.participantRole,
        user: p ? {
          id: p._id ?? '',
          username: p.username ?? '',
          display_name: p.displayName,
          profile_image_url: p.profileImageUrl,
        } : undefined,
      })),
      // Synthesize last_message from the flat fields that sendMessage writes to
      // the conversation doc. The UI sidebar uses this for the preview line and
      // fell back to "Start a conversation" without it.
      last_message: conv.lastMessagePreview
        ? ({
            id: '',
            conversation_id: conv._id,
            sender_id: conv.lastMessageSenderId ?? '',
            content: conv.lastMessagePreview,
            created_at: conv.lastMessageAt
              ? new Date(conv.lastMessageAt).toISOString()
              : new Date(conv.updatedAt ?? conv._creationTime).toISOString(),
          } as any)
        : undefined,
      unread_count: conv.unreadCount ?? 0,
    }));
  }, [conversationsData]);

  // Map active conversation
  const activeConversation: Conversation | null = useMemo(() => {
    if (!activeConvData) return null;
    return {
      id: activeConvData._id,
      created_at: new Date(activeConvData._creationTime).toISOString(),
      updated_at: new Date(activeConvData.updatedAt ?? activeConvData._creationTime).toISOString(),
      last_message_at: activeConvData.lastMessageAt ? new Date(activeConvData.lastMessageAt).toISOString() : undefined,
      conversation_type: activeConvData.conversationType,
      group_name: activeConvData.title,
      participants: (activeConvData.participants ?? []).map((p: any) => ({
        id: p._id ?? '',
        conversation_id: activeConvData._id,
        user_id: p.profileId ?? p.profile?._id ?? '',
        joined_at: '',
        is_active: true,
        role: p.role,
        user: p.profile ? {
          id: p.profile._id,
          username: p.profile.username ?? '',
          display_name: p.profile.displayName,
          profile_image_url: p.profile.profileImageUrl,
        } : undefined,
      })),
      unread_count: 0,
    };
  }, [activeConvData]);

  // Map messages
  const messages: Message[] = useMemo(() => {
    if (!messagesData?.page) return [];
    // Server returns newest-first via `.order("desc")` so it can paginate older
    // messages via cursor. The UI renders with .map() and expects chronological
    // ascending order (oldest at top, newest at bottom, auto-scroll to bottom).
    // Reverse the page so the chat flow reads top-to-bottom.
    return [...messagesData.page].reverse().map((msg: any) => ({
      id: msg._id,
      conversation_id: msg.conversationId,
      sender_id: msg.senderId,
      content: msg.content ?? '',
      created_at: new Date(msg._creationTime).toISOString(),
      edited_at: msg.editedAt ? new Date(msg.editedAt).toISOString() : undefined,
      is_deleted: !!msg.deletedAt,
      delivery_status: msg.deliveryStatus ?? 'sent',
      has_attachments: msg.hasAttachments,
      message_type: msg.messageType,
      metadata: msg.metadata,
      sender: msg.sender ? {
        id: msg.sender._id,
        username: msg.sender.username ?? '',
        display_name: msg.sender.displayName,
        profile_image_url: msg.sender.profileImageUrl,
      } : undefined,
      attachments: (msg.attachments ?? []).map((a: any) => ({
        id: a._id,
        message_id: msg._id,
        file_name: a.fileName,
        file_size: a.fileSize ?? 0,
        file_type: a.fileType ?? '',
        storage_path: '',
        // Server now resolves storageId → file_url; fall back to legacy
        // a.url for any rows the migration hasn't touched.
        url: a.file_url ?? a.url ?? '',
      })),
    }));
  }, [messagesData]);

  const isLoading = user ? conversationsData === undefined : false;
  const isLoadingMessages = activeConversationId ? messagesData === undefined : false;

  // Actions
  const loadConversations = useCallback(async () => {
    // Convex useQuery auto-refreshes
  }, []);

  const loadConversation = useCallback(async (conversationId: string) => {
    setActiveConversationId(conversationId);
  }, []);

  const loadMessages = useCallback(async (conversationId: string) => {
    setActiveConversationId(conversationId);
  }, []);

  const setActiveConversationFn = useCallback(async (conversationId: string) => {
    setActiveConversationId(conversationId);
    try {
      await markAsReadMutation({ conversationId: conversationId as any });
    } catch { /* ignore */ }
  }, [markAsReadMutation]);

  const sendMessage = useCallback(async (conversationId: string, content: string, _files?: File[]) => {
    setIsSendingMessage(true);
    try {
      await sendMessageMutation({
        conversationId: conversationId as any,
        content,
        messageType: 'text',
      });
    } finally {
      setIsSendingMessage(false);
    }
  }, [sendMessageMutation]);

  const createConversation = useCallback(async (
    conversationData: string[] | { participantIds: string[]; conversationType?: string; groupName?: string }
  ): Promise<Conversation | null> => {
    const participantIds = Array.isArray(conversationData)
      ? conversationData
      : conversationData.participantIds;
    const type = Array.isArray(conversationData)
      ? (participantIds.length === 1 ? 'direct' : 'group')
      : (conversationData.conversationType ?? 'direct');
    const title = !Array.isArray(conversationData) ? conversationData.groupName : undefined;

    const convId = await createConversationMutation({
      type: type as 'direct' | 'group',
      title,
      participantIds: participantIds as any[],
    });

    return {
      id: convId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      conversation_type: type as 'direct' | 'group',
      group_name: title,
      participants: [],
      unread_count: 0,
    };
  }, [createConversationMutation]);

  const markAsRead = useCallback(async (conversationId: string) => {
    try {
      await markAsReadMutation({ conversationId: conversationId as any });
    } catch { /* ignore */ }
  }, [markAsReadMutation]);

  const markMessagesAsRead = useCallback(async (conversationId: string, _messageIds: string[]) => {
    await markAsRead(conversationId);
  }, [markAsRead]);

  const clearActiveConversation = useCallback(() => {
    setActiveConversationId(null);
  }, []);

  const blockUser = useCallback(async (_userId: string) => {
    // Will be implemented via Convex mutation
  }, []);

  const unblockUser = useCallback(async (_userId: string) => {
    // Will be implemented via Convex mutation
  }, []);

  const reportMessage = useCallback(async (_conversationId: string, messageId: string | null, reason: string) => {
    if (!messageId) return;
    await reportMessageMutation({
      messageId: messageId as any,
      reason,
    });
  }, [reportMessageMutation]);

  const searchMessages = useCallback(async (_query: string): Promise<Message[]> => {
    return [];
  }, []);

  const deleteConversation = useCallback(async (conversationId: string) => {
    await archiveConversationMutation({ conversationId: conversationId as any });
  }, [archiveConversationMutation]);

  const sendTypingIndicator = useCallback((_conversationId: string) => {
    // Typing indicators handled via short-lived Convex mutations
  }, []);

  const archiveConversation = useCallback(async (conversationId: string) => {
    await archiveConversationMutation({ conversationId: conversationId as any });
  }, [archiveConversationMutation]);

  const unarchiveConversation = useCallback(async (conversationId: string) => {
    await archiveConversationMutation({ conversationId: conversationId as any });
  }, [archiveConversationMutation]);

  const getArchivedConversations = useCallback(async (): Promise<Conversation[]> => {
    return [];
  }, []);

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    await editMessageMutation({ id: messageId as any, content: newContent });
  }, [editMessageMutation]);

  const contextValue = useMemo<MessagingContextType>(() => ({
    conversations,
    activeConversation,
    messages,
    isLoading,
    isSendingMessage,
    isLoadingMessages,
    unreadCount: totalUnreadCount ?? 0,
    totalUnreadCount: totalUnreadCount ?? undefined,
    typingUsers: {},
    loadConversations,
    loadConversation,
    loadMessages,
    setActiveConversation: setActiveConversationFn,
    sendMessage,
    createConversation,
    markAsRead,
    markMessagesAsRead,
    clearActiveConversation,
    blockUser,
    unblockUser,
    reportMessage,
    searchMessages,
    deleteConversation,
    sendTypingIndicator,
    archiveConversation,
    unarchiveConversation,
    getArchivedConversations,
    editMessage,
  }), [
    conversations, activeConversation, messages, isLoading, isSendingMessage,
    isLoadingMessages, totalUnreadCount, loadConversations, loadConversation,
    loadMessages, setActiveConversationFn, sendMessage, createConversation,
    markAsRead, markMessagesAsRead, clearActiveConversation, blockUser,
    unblockUser, reportMessage, searchMessages, deleteConversation,
    sendTypingIndicator, archiveConversation, unarchiveConversation,
    getArchivedConversations, editMessage,
  ]);

  return (
    <MessagingContext.Provider value={contextValue}>
      {children}
    </MessagingContext.Provider>
  );
};
